import { StreamEvent, TokenUsage } from '@/apiTypes/AiTypes';

export interface SSEParserCallbacks {
    onTextDelta: (text: string) => void;
    onMetadata?: (metadata: { model: string; conversationId: string; usage: TokenUsage; finishReason: string }) => void;
    onError: (error: { message: string; code: string }) => void;
    onDone: (messageId: string) => void;
    onReplace?: (message: string) => void;
}

export const createSSEParser = (callbacks: SSEParserCallbacks) => {
    const decoder = new TextDecoder();
    let buffer = '';
    // The SSE `event:` field of the event being read. Events normally say what they are in the JSON's
    // `type`; one that does not (kuder-ai sends `event: error` with `{ message, code }` and no `type`)
    // is taken from this, so an error is never dropped silently.
    let eventName: string | null = null;
    // Whether the stream reported how it ended (`done` or `error`). See `ended`.
    let ended = false;

    const processLine = (line: string) => {
        if (line.startsWith('event:')) {
            eventName = line.slice(6).trim();
            return;
        }
        if (line.startsWith('data: ')) {
            const data = line.slice(6);
            const namedEvent = eventName;
            eventName = null;

            try {
                const parsed = JSON.parse(data) as StreamEvent | { type?: undefined; message?: string; code?: string };
                const event = (parsed.type === undefined && namedEvent === 'error'
                    ? { type: 'error', message: parsed.message ?? 'Stream failed', code: parsed.code ?? 'STREAM_ERROR' }
                    : parsed) as StreamEvent;

                if (event.type === 'done' || event.type === 'error') ended = true;

                switch (event.type) {
                    case 'text_delta':
                        callbacks.onTextDelta(event.text);
                        break;
                    case 'metadata':
                        callbacks.onMetadata?.({
                            model: event.model,
                            conversationId: event.conversationId,
                            usage: event.usage,
                            finishReason: event.finishReason,
                        });
                        break;
                    case 'error':
                        callbacks.onError({
                            message: event.message,
                            code: event.code,
                        });
                        break;
                    case 'done':
                        callbacks.onDone(event.messageId);
                        break;
                    case 'replace':
                        callbacks.onReplace?.(event.message);
                        break;
                }
            }
            catch {
                ended = true;
                callbacks.onError({
                    message: `Failed to parse SSE event data: ${data}`,
                    code: 'PARSE_ERROR',
                });
            }
        }
    };

    return {
        feed(chunk: Uint8Array) {
            buffer += decoder.decode(chunk, { stream: true });

            const lines = buffer.split('\n');
            // Keep the last potentially incomplete line in the buffer
            buffer = lines.pop() || '';

            for (const line of lines) {
                const trimmed = line.trim();
                if (trimmed) {
                    processLine(trimmed);
                }
            }
        },

        flush() {
            // Process any remaining data in the buffer
            if (buffer.trim()) {
                processLine(buffer.trim());
            }
            buffer = '';
        },

        /** True once the stream has said how it ended (a `done` or an `error`, including a parse error). */
        get ended() {
            return ended;
        },
    };
};
